<template>
  <component
    :is="embedded ? 'div' : 'v-dialog'"
    v-bind="wrapperBind"
    @update:model-value="onWrapperModelUpdate"
  >
    <div class="beneficiary-manage-modal" :class="{ embedded }">
      <div v-if="!embedded" class="modal-header beneficiary">
        <div class="header-icon">
          <v-icon size="24" color="white">mdi-cash-register</v-icon>
        </div>
        <div class="header-text">
          <h3>수납관리</h3>
          <p>총 {{ stats.total }}명</p>
        </div>
        <div class="header-spacer"></div>
        <button class="close-btn" @click="closeModal">
          <v-icon size="20">mdi-close</v-icon>
        </button>
      </div>

      <div class="modal-body beneficiary-body">
        <!-- ==================== 수납관리 ==================== -->
          <!-- 월 표시 + 검색 + 요약 -->
          <div class="sub-header collection-sub-header">
            <div class="sub-header-left">
              <div class="month-nav">
                <button type="button" class="nav-btn prev" @click="prevMonth" title="이전 월">
                  <v-icon size="18">mdi-chevron-left</v-icon>
                </button>
                <button type="button" class="month-picker-btn" @click="openCollectionMonthPicker">
                  <v-icon size="14" color="#64748b">mdi-calendar-month</v-icon>
                  <span class="current-month">{{ collectionYear }}년 {{ collectionMonth }}월</span>
                </button>
                <button type="button" class="nav-btn next" @click="nextMonth" title="다음 월">
                  <v-icon size="18">mdi-chevron-right</v-icon>
                </button>
                <input 
                  type="month" 
                  ref="collectionMonthPickerRef"
                  :value="collectionMonthValue"
                  @change="onCollectionMonthChange"
                  class="hidden-month-picker"
                />
              </div>
              <div class="beneficiary-search">
                <v-icon size="16" color="#64748b">mdi-magnify</v-icon>
                <input 
                  type="text"
                  v-model="collectionSearchName"
                  placeholder="수급자 검색..."
                  class="search-input"
                  @keydown.escape="collectionSearchName = ''"
                />
                <button 
                  v-if="collectionSearchName" 
                  class="search-clear-btn"
                  @click="collectionSearchName = ''"
                >
                  <v-icon size="14">mdi-close</v-icon>
                </button>
              </div>
            </div>
            <div class="collection-summary">
              <span class="summary-item">
                <span class="label">청구</span>
                <span class="value charge">{{ formatMoney(collectionSummary.totalCharge) }}</span>
              </span>
              <span class="summary-item">
                <span class="label">입금</span>
                <span class="value deposit">{{ formatMoney(collectionSummary.totalDeposit) }}</span>
              </span>
              <span class="summary-item">
                <span class="label">당월미수</span>
                <span 
                  class="value" 
                  :class="{ 
                    'unpaid': collectionSummary.currentUnpaid > 0, 
                    'overpaid': collectionSummary.currentOverpaid > 0 
                  }"
                >
                  <template v-if="collectionSummary.currentUnpaid > 0">
                    {{ formatMoney(collectionSummary.currentUnpaid) }}
                  </template>
                  <template v-else-if="collectionSummary.currentOverpaid > 0">
                    +{{ formatMoney(collectionSummary.currentOverpaid) }}
                  </template>
                  <template v-else>0원</template>
                </span>
              </span>
              <span class="summary-item cumulative-unpaid-item" @click="openUnpaidHistoryDialog">
                <span class="label">누적미수</span>
                <span 
                  class="value clickable" 
                  :class="{ 
                    'unpaid': cumulativeUnpaidTotal.unpaid > 0, 
                    'overpaid': cumulativeUnpaidTotal.overpaid > 0 
                  }"
                  title="클릭하여 미수이력 보기"
                >
                  <template v-if="cumulativeUnpaidTotal.unpaid > 0">
                    {{ formatMoney(cumulativeUnpaidTotal.unpaid) }}
                    <v-icon size="12" class="history-icon">mdi-chevron-right</v-icon>
                  </template>
                  <template v-else-if="cumulativeUnpaidTotal.overpaid > 0">
                    +{{ formatMoney(cumulativeUnpaidTotal.overpaid) }}
                    <v-icon size="12" class="history-icon">mdi-chevron-right</v-icon>
                  </template>
                  <template v-else>0원</template>
                </span>
              </span>
            </div>
            <div class="collection-actions">
              <button 
                v-if="selectedBeneficiaries.size > 0"
                class="btn-delete-selected"
                @click="deleteSelectedBeneficiaries"
                :disabled="isDeletingCharges"
              >
                <v-progress-circular v-if="isDeletingCharges" indeterminate size="14" width="2" />
                <v-icon v-else size="14">mdi-delete</v-icon>
                <span>선택 삭제 ({{ selectedBeneficiaries.size }}명)</span>
              </button>
              <button 
                v-if="collectionData.length > 0"
                class="btn-delete-all"
                @click="deleteAllBeneficiaries"
                :disabled="isDeletingCharges"
              >
                <v-icon size="14">mdi-delete-sweep</v-icon>
                <span>전체 삭제</span>
              </button>
            </div>
          </div>

          <!-- 수금 테이블 -->
          <div class="collection-table-wrap">
            <table class="collection-table">
              <thead>
                <tr>
                  <th class="col-check">
                    <input 
                      type="checkbox" 
                      :checked="isAllSelected"
                      :indeterminate="isPartialSelected"
                      @change="toggleSelectAll"
                      title="전체 선택"
                    />
                  </th>
                  <th class="col-ltc">인정번호</th>
                  <th class="col-name sortable" @click="toggleCollectionSort('cb_name')">
                    수급자
                    <v-icon size="14" class="sort-icon" :class="{ 'active': collectionSortKey === 'cb_name' }">
                      {{ collectionSortKey === 'cb_name' ? (collectionSortOrder === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down') : 'mdi-arrow-up-down' }}
                    </v-icon>
                  </th>
                  <th class="col-type">구분</th>
                  <th class="col-item">항목</th>
                  <th class="col-charge">청구금액</th>
                  <th class="col-charge-date">청구일</th>
                  <th class="col-deposit">입금액</th>
                  <th class="col-deposit-date">입금일</th>
                  <th class="col-unpaid">미수금액</th>
                  <th class="col-actions">관리</th>
                </tr>
              </thead>
              <tbody>
                <template v-if="collectionLoading">
                  <tr>
                    <td colspan="11" class="loading-cell">
                      <v-progress-circular indeterminate size="24" color="#0d9488" />
                    </td>
                  </tr>
                </template>
                <template v-else-if="sortedCollectionData.length === 0">
                  <tr>
                    <td colspan="11" class="empty-cell">
                      <p>수납 데이터가 없습니다.</p>
                      <p class="hint">수납관리 엑셀 업로드 또는 통장내역에서 본인부담금수입에 수급자를 연결하세요.</p>
                    </td>
                  </tr>
                </template>
                <template v-else>
                  <template v-for="(ben, benIdx) in sortedCollectionData" :key="ben.cb_idx">
                    <!-- 수급자별 청구 항목들 -->
                    <tr 
                      v-for="(charge, chargeIdx) in ben.charges" 
                      :key="`${ben.cb_idx}-${chargeIdx}`"
                      class="data-row"
                      :class="{ 'first-row': chargeIdx === 0, 'no-ltc': !hasValidLtcNo(ben), 'selected': selectedBeneficiaries.has(ben.cb_idx) }"
                    >
                      <!-- 체크박스 (수급자 단위로 선택, 첫 행에만 표시) -->
                      <!-- 통장 매칭된 본인부담금이 있는 수급자는 체크 불가 -->
                      <td 
                        v-if="chargeIdx === 0" 
                        class="col-check"
                        :rowspan="ben.charges.length"
                      >
                        <input 
                          v-if="!hasMatchedBenefit(ben)"
                          type="checkbox" 
                          :checked="selectedBeneficiaries.has(ben.cb_idx)"
                          @change="toggleBeneficiarySelection(ben.cb_idx)"
                        />
                        <span v-else class="matched-lock" title="통장 매칭된 본인부담금은 삭제할 수 없습니다">
                          <v-icon size="14" color="#94a3b8">mdi-lock</v-icon>
                        </span>
                      </td>
                      <!-- 인정번호 (첫 행에만) -->
                      <td 
                        v-if="chargeIdx === 0" 
                        class="col-ltc" 
                        :rowspan="ben.charges.length"
                      >
                        <template v-if="hasValidLtcNo(ben)">
                          <span class="ltc-no">{{ ben.cb_ltc_no }}</span>
                        </template>
                        <template v-else>
                          <span v-if="isTempLtcNo(ben.cb_ltc_no || '')" class="temp-badge">임시</span>
                          <div class="ltc-input-wrap" :class="{ 'saving': ltcSavingIdx === ben.cb_idx }">
                            <span class="ltc-prefix">L</span>
                            <input 
                              type="text"
                              v-model="ltcInputValues[ben.cb_idx]"
                              @keydown.enter="saveLtcNo(ben)"
                              @blur="saveLtcNo(ben)"
                              class="ltc-input"
                              placeholder="10자리 숫자"
                              maxlength="10"
                              @input="ltcInputValues[ben.cb_idx] = (ltcInputValues[ben.cb_idx] || '').replace(/[^0-9]/g, '')"
                            />
                            <v-progress-circular 
                              v-if="ltcSavingIdx === ben.cb_idx" 
                              indeterminate 
                              size="14" 
                              width="2"
                              color="#0d9488"
                            />
                          </div>
                          <span class="ltc-hint">정식 인정번호 입력 후 수금 관리 가능</span>
                        </template>
                      </td>
                      <!-- 수급자명 (첫 행에만) -->
                      <td 
                        v-if="chargeIdx === 0" 
                        class="col-name" 
                        :rowspan="ben.charges.length"
                      >
                        <span class="beneficiary-name">{{ ben.cb_name }}</span>
                        <span 
                          v-if="getBeneficiaryStatus(ben)" 
                          class="status-badge" 
                          :class="getBeneficiaryStatus(ben)?.class"
                        >{{ getBeneficiaryStatus(ben)?.text }}</span>
                      </td>
                      <!-- 구분 -->
                      <td class="col-type">
                        <span class="type-badge" :class="charge.cc_scode === '112' ? 'benefit' : 'nonbenefit'">
                          {{ charge.cc_scode === '112' ? '급여' : '비급여' }}
                        </span>
                      </td>
                      <!-- 항목 -->
                      <td class="col-item">{{ charge.cc_sname }}</td>
                      <!-- 청구금액 -->
                      <td class="col-charge" :class="{ 'needs-charge': ben.hasDeposit && charge.cc_scode === '112' && !charge.cc_charge }">
                        <input 
                          type="text"
                          :value="formatNumber(charge.cc_charge)"
                          @input="onChargeInput(ben.cb_idx, chargeIdx, $event)"
                          @blur="onChargeBlur(ben.cb_idx, chargeIdx)"
                          class="amount-input"
                          :class="{ 
                            'disabled': !hasValidLtcNo(ben), 
                            'saving': charge._saving,
                            'warning': ben.hasDeposit && charge.cc_scode === '112' && !charge.cc_charge
                          }"
                          :disabled="!hasValidLtcNo(ben)"
                          :placeholder="ben.hasDeposit && charge.cc_scode === '112' && !charge.cc_charge ? '청구금액 입력!' : '0'"
                        />
                      </td>
                      <!-- 청구일 -->
                      <td class="col-charge-date">
                        <YmdDateInput
                          :model-value="charge.cc_charge_date || ''"
                          class="charge-date-input"
                          :disabled="!hasValidLtcNo(ben)"
                          @update:model-value="(v) => onChargeDateChange(ben.cb_idx, chargeIdx, { target: { value: v } })"
                        />
                      </td>
                      <!-- 입금액 (자동 분배 - 읽기전용) -->
                      <td class="col-deposit">
                        <template v-if="charge.cc_deposit > 0">
                          <!-- 상세 내역이 있으면 분리 표시 -->
                          <template v-if="charge.cc_deposit_detail && charge.cc_deposit_detail.length > 1">
                            <div class="deposit-details">
                              <div 
                                v-for="(detail, dIdx) in charge.cc_deposit_detail" 
                                :key="dIdx"
                                class="deposit-detail-row"
                              >
                                <span class="detail-amount">{{ formatNumber(detail.amount) }}</span>
                              </div>
                            </div>
                          </template>
                          <template v-else>
                            <span 
                              class="deposit-value"
                              :class="{
                                'has-unpaid': charge.cc_unpaid > 0,
                                'has-overpaid': charge.cc_overpaid > 0
                              }"
                            >{{ formatNumber(charge.cc_deposit) }}</span>
                          </template>
                        </template>
                        <template v-else>
                          <span class="no-deposit-hint">-</span>
                        </template>
                      </td>
                      <!-- 입금일 (통장내역 날짜 - 읽기전용) -->
                      <td class="col-deposit-date">
                        <template v-if="charge.cc_deposit > 0">
                          <!-- 상세 내역이 있으면 날짜별 표시 -->
                          <template v-if="charge.cc_deposit_detail && charge.cc_deposit_detail.length > 1">
                            <div class="deposit-details">
                              <div 
                                v-for="(detail, dIdx) in charge.cc_deposit_detail" 
                                :key="dIdx"
                                class="deposit-detail-row"
                              >
                                <span class="detail-date">{{ formatShortDate(detail.date) }}</span>
                              </div>
                            </div>
                          </template>
                          <template v-else>
                            <span class="deposit-date-value">{{ charge.cc_deposit_date ? formatShortDate(charge.cc_deposit_date) : '-' }}</span>
                          </template>
                        </template>
                        <template v-else>
                          <span class="no-deposit-hint">-</span>
                        </template>
                      </td>
                      <!-- 미수금액 / 초과입금 -->
                      <td class="col-unpaid" :class="{ 'has-unpaid': charge.cc_unpaid > 0, 'has-overpaid': charge.cc_overpaid > 0 }">
                        <template v-if="charge.cc_unpaid > 0">
                          {{ formatMoney(charge.cc_unpaid) }}
                        </template>
                        <template v-else-if="charge.cc_overpaid > 0">
                          +{{ formatMoney(charge.cc_overpaid) }}
                        </template>
                        <template v-else>0원</template>
                      </td>
                      <!-- 관리 -->
                      <td class="col-actions">
                        <div class="action-btns">
                          <!-- 본인부담금(첫 행): + 버튼으로 항목 추가 -->
                          <div 
                            v-if="charge.cc_scode === '112' && hasValidLtcNo(ben) && getAvailableItems(ben).length > 0" 
                            class="add-item-dropdown"
                          >
                            <button 
                              class="btn-add-item"
                              @click="toggleAddDropdown(ben.cb_idx)"
                              title="항목 추가"
                            >
                              <v-icon size="14">mdi-plus</v-icon>
                            </button>
                            <div 
                              v-if="addDropdownOpen === ben.cb_idx" 
                              class="dropdown-menu"
                            >
                              <button 
                                v-for="fc in getAvailableItems(ben)" 
                                :key="fc.scode"
                                class="dropdown-item"
                                @click="selectAddItem(ben.cb_idx, fc)"
                              >
                                {{ fc.sname }}
                              </button>
                            </div>
                          </div>
                          <!-- 삭제 버튼 -->
                          <!-- 매칭된 본인부담금수입은 삭제 버튼 숨김 -->
                          <button 
                            v-if="hasValidLtcNo(ben) && !(charge.cc_scode === '112' && ben.hasDeposit)" 
                            class="btn-remove-charge"
                            :class="{ 'benefit': charge.cc_scode === '112' }"
                            @click="removeCharge(ben.cb_idx, chargeIdx)"
                            :title="charge.cc_scode === '112' ? '본인부담금 삭제' : '항목 삭제'"
                          >
                            <v-icon size="14">mdi-close</v-icon>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </template>
                </template>
              </tbody>
              <tfoot v-if="collectionData.length > 0">
                <tr class="total-row">
                  <td class="col-check"></td>
                  <td class="col-ltc"></td>
                  <td class="col-name">합계</td>
                  <td class="col-type"></td>
                  <td class="col-item"></td>
                  <td class="col-charge total">{{ formatMoney(collectionSummary.totalCharge) }}</td>
                  <td class="col-charge-date"></td>
                  <td class="col-deposit total">{{ formatMoney(collectionSummary.totalDeposit) }}</td>
                  <td class="col-deposit-date"></td>
                  <td class="col-unpaid total" :class="{ 'has-unpaid': collectionSummary.totalUnpaid > 0, 'has-overpaid': collectionSummary.totalOverpaid > 0 }">
                    <template v-if="collectionSummary.totalUnpaid > 0">
                      {{ formatMoney(collectionSummary.totalUnpaid) }}
                    </template>
                    <template v-else-if="collectionSummary.totalOverpaid > 0">
                      +{{ formatMoney(collectionSummary.totalOverpaid) }}
                    </template>
                    <template v-else>0원</template>
                  </td>
                  <td class="col-actions"></td>
                </tr>
              </tfoot>
            </table>
          </div>
      </div>

      <div v-if="!embedded" class="modal-footer">
        <span class="footer-hint">수금 데이터는 통장내역과 연동됩니다</span>
        <button type="button" class="btn-close" @click="closeModal">닫기</button>
      </div>
    </div>

    <!-- 미수이력 다이얼로그 -->
    <v-dialog v-model="unpaidHistoryDialog" max-width="900" class="unpaid-history-dialog-wrapper">
      <div class="unpaid-history-dialog">
        <div class="dialog-header">
          <div class="header-title">
            <v-icon size="20" color="#ca8a04">mdi-history</v-icon>
            <h3>미수금 이력</h3>
            <span class="total-unpaid" v-if="unpaidHistoryData.totalUnpaid > 0">
              총 {{ formatMoney(unpaidHistoryData.totalUnpaid) }}
            </span>
          </div>
          <button class="close-btn" @click="unpaidHistoryDialog = false">
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </div>

        <div class="dialog-body">
          <!-- 검색 영역 -->
          <div class="search-filters">
            <div class="filter-row">
              <div class="filter-item name-search">
                <label>수급자명</label>
                <div class="search-input-wrap">
                  <v-icon size="16" color="#64748b">mdi-magnify</v-icon>
                  <input 
                    type="text"
                    v-model="unpaidHistorySearch.name"
                    placeholder="수급자명 검색..."
                    @keydown.enter="searchUnpaidHistory"
                  />
                  <button 
                    v-if="unpaidHistorySearch.name" 
                    class="clear-btn"
                    @click="unpaidHistorySearch.name = ''; searchUnpaidHistory()"
                  >
                    <v-icon size="12">mdi-close</v-icon>
                  </button>
                </div>
              </div>
              <div class="filter-item date-range">
                <label>청구일 기간</label>
                <div class="date-inputs">
                  <YmdDateInput v-model="unpaidHistorySearch.startDate" />
                  <span class="date-separator">~</span>
                  <YmdDateInput v-model="unpaidHistorySearch.endDate" />
                </div>
              </div>
              <div class="filter-actions">
                <button class="btn-search" @click="searchUnpaidHistory">
                  <v-icon size="14">mdi-magnify</v-icon>
                  검색
                </button>
                <button class="btn-reset" @click="resetUnpaidHistorySearch">
                  <v-icon size="14">mdi-refresh</v-icon>
                  초기화
                </button>
              </div>
            </div>
          </div>

          <!-- 미수 내역 테이블 -->
          <div class="history-table-wrap">
            <table class="history-table">
              <thead>
                <tr>
                  <th class="col-name">수급자명</th>
                  <th class="col-year-month">청구연월</th>
                  <th class="col-charge-date">청구일</th>
                  <th class="col-item">항목</th>
                  <th class="col-charge">청구금액</th>
                  <th class="col-deposit">입금금액</th>
                  <th class="col-unpaid">미수금액</th>
                </tr>
              </thead>
              <tbody>
                <template v-if="unpaidHistoryLoading">
                  <tr>
                    <td colspan="7" class="loading-cell">
                      <v-progress-circular indeterminate size="24" color="#ca8a04" />
                    </td>
                  </tr>
                </template>
                <template v-else-if="unpaidHistoryData.list.length === 0">
                  <tr>
                    <td colspan="7" class="empty-cell">
                      미수금 내역이 없습니다.
                    </td>
                  </tr>
                </template>
                <template v-else>
                  <tr v-for="item in unpaidHistoryData.list" :key="item.cc_idx" class="data-row">
                    <td class="col-name">
                      <span class="beneficiary-name">{{ item.cb_name }}</span>
                    </td>
                    <td class="col-year-month">{{ item.cc_year }}년 {{ item.cc_month }}월</td>
                    <td class="col-charge-date">{{ formatDateKorean(item.cc_charge_date) }}</td>
                    <td class="col-item">{{ item.cc_sname }}</td>
                    <td class="col-charge">{{ formatMoney(item.cc_charge) }}</td>
                    <td class="col-deposit">{{ formatMoney(item.cc_deposit) }}</td>
                    <td class="col-unpaid">{{ formatMoney(item.cc_unpaid) }}</td>
                  </tr>
                </template>
              </tbody>
              <tfoot v-if="unpaidHistoryData.list.length > 0">
                <tr class="total-row">
                  <td colspan="6" class="total-label">합계</td>
                  <td class="col-unpaid total">{{ formatMoney(unpaidHistoryData.totalUnpaid) }}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <!-- 페이지네이션 -->
          <div class="pagination-area" v-if="unpaidHistoryData.totalPages > 1">
            <span class="page-info">
              {{ unpaidHistoryData.total }}건 중 {{ (unpaidHistoryData.page - 1) * 50 + 1 }}-{{ Math.min(unpaidHistoryData.page * 50, unpaidHistoryData.total) }}
            </span>
            <div class="page-btns">
              <button 
                :disabled="unpaidHistoryData.page <= 1"
                @click="loadUnpaidHistory(unpaidHistoryData.page - 1)"
              >
                <v-icon size="16">mdi-chevron-left</v-icon>
              </button>
              <span class="current-page">{{ unpaidHistoryData.page }} / {{ unpaidHistoryData.totalPages }}</span>
              <button 
                :disabled="unpaidHistoryData.page >= unpaidHistoryData.totalPages"
                @click="loadUnpaidHistory(unpaidHistoryData.page + 1)"
              >
                <v-icon size="16">mdi-chevron-right</v-icon>
              </button>
            </div>
          </div>
        </div>

        <div class="dialog-footer">
          <button type="button" class="btn-close" @click="unpaidHistoryDialog = false">닫기</button>
        </div>
      </div>
    </v-dialog>
  </component>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue';
import YmdDateInput from '@/components/common/YmdDateInput.vue';
import { beneficiaryApi, collectionApi } from '@/services/api';

const props = withDefaults(defineProps<{
  modelValue?: boolean;
  memberCode: string | number;
  year?: number | string;
  month?: string;
  initialSearchName?: string;
  depositData?: Record<number, { amount: number; date: string }>;  // 통장내역 본인부담금 매칭 데이터
  bizGubun?: string;  // 선택된 사업구분 (통장에서 전달)
  embedded?: boolean; // 탭 본문에 인라인 표시
}>(), {
  modelValue: false,
  embedded: false,
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'beneficiaryChanged'): void;
  (e: 'collectionDeleted'): void;  // 수납 데이터 삭제 시 (매칭 새로고침 필요)
  (e: 'chargeUpdated', cbIdx: number): void;  // 청구금액 변경 시 (입금 재분배 필요)
}>();

interface BeneficiaryData {
  cb_idx: number;
  m_code: number;
  cb_ltc_no: string;
  cb_name: string;
  cb_status: string;
  cb_leave_date: string | null;
  cb_guardian1: string | null;
  cb_guardian2: string | null;
  cb_guardian3: string | null;
  cb_memo: string | null;
  cb_wdate: string;
  cb_rdate: string | null;
}

interface DepositDetail {
  date: string;
  amount: number;
}

interface ChargeItem {
  cc_idx?: number;          // DB PK (신규는 undefined)
  cc_scode: string;         // 계정과목 소분류 코드 (112, 113, 114, 115)
  cc_sname: string;         // 계정과목 소분류명 (본인부담금수입, 식재료비수입 등)
  cc_charge: number;
  cc_charge_date: string;   // 'YYYY-MM-DD' 또는 ''
  cc_deposit: number;
  cc_deposit_date: string;  // 'YYYY-MM-DD' 또는 ''
  cc_deposit_detail?: DepositDetail[];  // 입금 상세 내역 [{date, amount}, ...]
  cc_unpaid: number;        // 미수금 (청구 > 입금일 때만, 0 이상)
  cc_overpaid: number;      // 초과입금 (입금 > 청구일 때만, 0 이상)
  cc_memo?: string;         // 적요 (수급자명 + 항목명)
  _dirty?: boolean;         // 변경됨 플래그
  _saving?: boolean;        // 저장중 플래그
  _hasDeposit?: boolean;    // 통장 매칭 여부 (본인부담금 입금 있음)
}

interface FinanceCode {
  scode: string;
  sname: string;
}

interface CollectionBeneficiary {
  cb_idx: number;
  cb_name: string;
  cb_ltc_no?: string;
  cb_status?: string;
  cb_leave_date?: string | null;
  charges: ChargeItem[];
  totalCharge: number;
  totalDeposit: number;
  totalUnpaid: number;       // 총 미수금 (0 이상)
  totalOverpaid: number;     // 총 초과입금 (0 이상)
  hasDeposit?: boolean;      // 통장 매칭 여부 (본인부담금 입금 있음)
  depositAmount?: number;    // 통장에서 가져온 입금액
  depositDate?: string;      // 통장에서 가져온 입금일
  prevUnpaid?: number;       // 이전 월 이월 미수금
  prevOverpaid?: number;     // 이전 월 이월 초과입금
  cumulativeUnpaid?: number; // 누적 미수금 (이월 + 당월)
  cumulativeOverpaid?: number; // 누적 초과입금 (이월 + 당월)
}

// Refs
const collectionMonthPickerRef = ref<HTMLInputElement | null>(null);

// 상태
const dialogOpen = ref(false);

const wrapperBind = computed(() => {
  if (props.embedded) {
    return { class: 'beneficiary-embed-host' };
  }
  return {
    modelValue: dialogOpen.value,
    maxWidth: 1100,
    persistent: true,
  };
});

const onWrapperModelUpdate = (val: boolean) => {
  if (props.embedded) return;
  dialogOpen.value = val;
};

const isActive = computed(() => props.embedded || dialogOpen.value);
const list = ref<BeneficiaryData[]>([]);
const stats = ref({ total: 0, active: 0, inactive: 0 });
const loading = ref(false);

// 수납관리
const collectionLoading = ref(false);
const collectionData = ref<CollectionBeneficiary[]>([]);
const financeCodes = ref<FinanceCode[]>([]);  // 계정과목 목록 (API에서 로드)
const collectionSearchName = ref('');
const addDropdownOpen = ref<number | null>(null);  // 항목추가 드롭다운 열림 상태 (cb_idx)

// 수납관리 정렬
const collectionSortKey = ref<string>('cb_name');
const collectionSortOrder = ref<'asc' | 'desc'>('asc');

// 인정번호 입력 관련
const ltcInputValues = ref<Record<number, string>>({});
const ltcSavingIdx = ref<number | null>(null);

// 선택/삭제 관련 (수급자 단위)
const selectedBeneficiaries = ref<Set<number>>(new Set());
const isDeletingCharges = ref(false);

// 전체 누적 미수금
const cumulativeUnpaidTotal = ref({ unpaid: 0, overpaid: 0 });

// 미수이력 다이얼로그
const unpaidHistoryDialog = ref(false);
const unpaidHistoryLoading = ref(false);
const unpaidHistorySearch = ref({
  name: '',
  startDate: '',
  endDate: ''
});
const unpaidHistoryData = ref<{
  list: any[];
  total: number;
  totalUnpaid: number;
  page: number;
  totalPages: number;
}>({
  list: [],
  total: 0,
  totalUnpaid: 0,
  page: 1,
  totalPages: 1
});

// 수납관리 연월 (로컬 상태, 초기값은 통장내역에서)
const collectionYear = ref(new Date().getFullYear());
const collectionMonth = ref(new Date().getMonth() + 1);

// 월 피커용 값 (YYYY-MM 형식)
const collectionMonthValue = computed(() => {
  const m = String(collectionMonth.value).padStart(2, '0');
  return `${collectionYear.value}-${m}`;
});

// 입금일 기본 prefix (예: "2026-03-")
const datePrefix = computed(() => {
  const m = String(collectionMonth.value).padStart(2, '0');
  return `${collectionYear.value}-${m}-`;
});

// 월 피커 열기
const openCollectionMonthPicker = () => {
  collectionMonthPickerRef.value?.showPicker();
};

// 현재 연월이 props로 받은 연월(통장내역 연월)과 같은지 확인
const isCurrentMonth = computed(() => {
  const propsYear = props.year ? (typeof props.year === 'string' ? parseInt(props.year) : props.year) : null;
  const propsMonth = props.month ? parseInt(props.month) : null;
  return propsYear === collectionYear.value && propsMonth === collectionMonth.value;
});

// 현재 조회 연월 기준으로 퇴소 여부 판단
// 퇴소일이 조회 연월의 마지막 날 이전이면 퇴소 상태
const wasInactiveAt = (leaveDate: string | null | undefined): boolean => {
  if (!leaveDate) return false;
  
  // 조회 연월의 마지막 날 계산
  const lastDayOfMonth = new Date(collectionYear.value, collectionMonth.value, 0);
  const leaveDateObj = new Date(leaveDate);
  
  // 퇴소일이 조회 연월의 마지막 날보다 이전이면 퇴소 상태
  return leaveDateObj < lastDayOfMonth;
};

// 수급자의 상태 뱃지 텍스트 및 클래스 반환
const getBeneficiaryStatus = (ben: CollectionBeneficiary): { text: string; class: string } | null => {
  if (wasInactiveAt(ben.cb_leave_date)) {
    return { text: '퇴소', class: 'inactive' };
  }
  return null;
};

// 이전 월로 이동
const prevMonth = () => {
  if (collectionMonth.value === 1) {
    collectionYear.value--;
    collectionMonth.value = 12;
  } else {
    collectionMonth.value--;
  }
  loadCollectionData();
};

// 다음 월로 이동
const nextMonth = () => {
  if (collectionMonth.value === 12) {
    collectionYear.value++;
    collectionMonth.value = 1;
  } else {
    collectionMonth.value++;
  }
  loadCollectionData();
};

// 월 변경 처리 (피커에서 선택)
const onCollectionMonthChange = (event: Event) => {
  const input = event.target as HTMLInputElement;
  if (input.value) {
    const [year, month] = input.value.split('-');
    collectionYear.value = parseInt(year);
    collectionMonth.value = parseInt(month);
    loadCollectionData();
  }
};

// 수금 요약 (computed)
const collectionSummary = computed(() => {
  let totalCharge = 0;
  let totalDeposit = 0;
  let totalPrevUnpaid = 0;
  let totalPrevOverpaid = 0;
  
  collectionData.value.forEach(ben => {
    totalCharge += ben.totalCharge || 0;
    totalDeposit += ben.totalDeposit || 0;
    totalPrevUnpaid += ben.prevUnpaid || 0;
    totalPrevOverpaid += ben.prevOverpaid || 0;
  });
  
  // 당월 순 미수/초과 계산
  const currentDiff = totalCharge - totalDeposit;
  const currentUnpaid = currentDiff > 0 ? currentDiff : 0;
  const currentOverpaid = currentDiff < 0 ? Math.abs(currentDiff) : 0;
  
  // 이월 순 미수금
  const prevNet = totalPrevUnpaid - totalPrevOverpaid;
  
  // 누적 미수금 (이월 + 당월)
  const cumulativeNet = prevNet + (currentUnpaid - currentOverpaid);
  const totalUnpaid = cumulativeNet > 0 ? cumulativeNet : 0;
  const totalOverpaid = cumulativeNet < 0 ? Math.abs(cumulativeNet) : 0;
  
  return { 
    totalCharge, 
    totalDeposit, 
    totalUnpaid,       // 누적 미수금
    totalOverpaid,     // 누적 초과입금
    currentUnpaid,     // 당월 미수금
    currentOverpaid,   // 당월 초과입금
    prevUnpaid: totalPrevUnpaid,   // 이월 미수금
    prevOverpaid: totalPrevOverpaid // 이월 초과입금
  };
});

// 필터링 및 정렬된 수금 데이터
const sortedCollectionData = computed(() => {
  let data = [...collectionData.value];
  
  // 수급자명 검색 필터
  if (collectionSearchName.value.trim()) {
    const query = collectionSearchName.value.trim().toLowerCase();
    data = data.filter(ben => ben.cb_name.toLowerCase().includes(query));
  }
  
  // 정렬
  if (collectionSortKey.value) {
    data.sort((a, b) => {
      const aVal = (a as any)[collectionSortKey.value] || '';
      const bVal = (b as any)[collectionSortKey.value] || '';
      
      if (collectionSortOrder.value === 'asc') {
        return String(aVal).localeCompare(String(bVal), 'ko');
      } else {
        return String(bVal).localeCompare(String(aVal), 'ko');
      }
    });
  }
  
  return data;
});

// 수납관리 정렬 토글
const toggleCollectionSort = (key: string) => {
  if (collectionSortKey.value === key) {
    collectionSortOrder.value = collectionSortOrder.value === 'asc' ? 'desc' : 'asc';
  } else {
    collectionSortKey.value = key;
    collectionSortOrder.value = 'asc';
  }
};

// 장기요양인정번호 형식: L + 10자리 숫자
const LTC_NO_REGEX = /^L\d{10}$/;

// 인정번호 유효성 검사
const isValidLtcNo = (value: string): boolean => {
  return LTC_NO_REGEX.test(value);
};

// 임시 인정번호 여부
const isTempLtcNo = (value: string): boolean => {
  return value.startsWith('L-TEMP-');
};


// 금액 포맷
const formatMoney = (amount: number | null | undefined): string => {
  if (!amount) return '-';
  return amount.toLocaleString() + '원';
};

const formatNumber = (amount: number | null | undefined): string => {
  if (!amount) return '';
  return amount.toLocaleString();
};

const formatShortDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '-';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return `${d.getMonth() + 1}/${d.getDate()}`;
  } catch {
    return dateStr;
  }
};

const parseNumber = (str: string): number => {
  return parseInt(str.replace(/[^0-9]/g, '')) || 0;
};

// 날짜 포맷 (YYYY-MM-DD → MM/DD)
const formatDateShort = (dateStr?: string): string => {
  if (!dateStr) return '-';
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    return `${parseInt(parts[1])}/${parseInt(parts[2])}`;
  }
  return dateStr;
};

// 입금일에서 일자만 추출
const getDateDay = (dateStr: string): string => {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    return String(parseInt(parts[2])); // 앞자리 0 제거
  }
  return '';
};

// 청구일 변경 처리 (date picker)
const onChargeDateChange = (cbIdx: number, chargeIdx: number, event: Event) => {
  const input = event.target as HTMLInputElement;
  const ben = collectionData.value.find(b => b.cb_idx === cbIdx);
  if (!ben || !ben.charges[chargeIdx]) return;
  
  ben.charges[chargeIdx].cc_charge_date = input.value || '';
  ben.charges[chargeIdx]._dirty = true;
  saveCharge(cbIdx, ben.charges[chargeIdx]);
};

// 외부 클릭 시 드롭다운 닫기
const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement;
  if (!target.closest('.add-item-dropdown')) {
    addDropdownOpen.value = null;
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});

// 초기화 (watch보다 먼저 정의되어야 함)
const resetAll = () => {
  // 수급자 검색어 초기화 (props에서 전달된 경우 사용)
  collectionSearchName.value = props.initialSearchName || '';
  
  // 수납관리 연월을 통장내역 연월로 초기화
  if (props.year) collectionYear.value = typeof props.year === 'string' ? parseInt(props.year) : props.year;
  if (props.month) collectionMonth.value = parseInt(props.month);
  
  // 선택 상태 초기화
  selectedBeneficiaries.value.clear();
};

// 닫기
const closeModal = () => {
  dialogOpen.value = false;
};

// 계정과목 로드 (본인부담금 분개 항목들)
const loadFinanceCodes = async () => {
  try {
    const res = await collectionApi.getFinanceCodes(collectionYear.value);
    financeCodes.value = res.data?.data || [];
  } catch (error) {
    console.error('계정과목 로드 오류:', error);
    // 기본값 설정 (API 실패 시)
    financeCodes.value = [
      { scode: '112', sname: '본인부담금수입' },
      { scode: '113', sname: '식재료비수입' },
      { scode: '114', sname: '상급침실이용료' },
      { scode: '115', sname: '이미용비' },
      { scode: '116', sname: '기타비급여수입' }
    ];
  }
};

// 비급여 항목 목록 (본인부담금 제외)
const nonBenefitItems = computed(() => {
  return financeCodes.value.filter(fc => fc.scode !== '112');
});

// 목록 로드
const loadList = async () => {
  if (!props.memberCode) return;
  loading.value = true;
  try {
    const params: { limit: number; bizGubun?: string } = { limit: 200 };
    if (props.bizGubun) {
      params.bizGubun = props.bizGubun;
    }
    const [listRes, statsRes] = await Promise.all([
      beneficiaryApi.getList(props.memberCode, params),
      beneficiaryApi.getStats(props.memberCode, props.bizGubun || undefined)
    ]);
    list.value = listRes.data?.data?.list || [];
    stats.value = statsRes.data?.data || { total: 0, active: 0, inactive: 0 };
  } catch (error) {
    console.error('수급자 목록 로드 오류:', error);
  } finally {
    loading.value = false;
  }
};

// 수금 데이터 로드
const loadCollectionData = async () => {
  collectionLoading.value = true;
  // 선택 상태 초기화 (월 변경 등)
  selectedBeneficiaries.value.clear();
  try {
    // 계정과목 로드 (연도 변경 시) + 전체 누적 미수금 로드
    await Promise.all([
      loadFinanceCodes(),
      loadCumulativeUnpaid()
    ]);
    
    // 통장내역 본인부담금 매칭 데이터 (props에서 전달)
    // 단, props로 받은 연월과 현재 선택된 연월이 같을 때만 사용
    const depositDataMap = isCurrentMonth.value ? (props.depositData || {}) : {};
    
    // API에서 시설의 해당 월 수금 데이터 조회 + 이전 월 미수금 조회 (병렬)
    const collectionParams: { year: number; month: number; bizGubun?: string } = {
      year: collectionYear.value,
      month: collectionMonth.value
    };
    if (props.bizGubun) {
      collectionParams.bizGubun = props.bizGubun;
    }
    const [res, prevUnpaidRes] = await Promise.all([
      collectionApi.getByMember(props.memberCode, collectionParams),
      collectionApi.getPreviousUnpaid(props.memberCode, {
        year: collectionYear.value,
        month: collectionMonth.value
      })
    ]);
    
    const dbItems = res.data?.data || [];
    const prevUnpaidData = prevUnpaidRes.data?.data || [];
    
    // 이전 월 미수금을 Map으로 변환
    const prevUnpaidMap = new Map<number, { unpaid: number; overpaid: number }>();
    prevUnpaidData.forEach((item: { cb_idx: number; prev_unpaid: number; prev_overpaid: number }) => {
      prevUnpaidMap.set(item.cb_idx, {
        unpaid: item.prev_unpaid || 0,
        overpaid: item.prev_overpaid || 0
      });
    });
    
    // DB에 수납 데이터가 있는 수급자 cb_idx 목록
    const dbBeneficiaryIds = new Set(dbItems.map((item: any) => item.cb_idx));
    
    // 통장 본인부담금수입에 연결된 수급자 목록
    const linkedCbIdxSet = new Set(Object.keys(depositDataMap).map(Number));
    
    // 표시할 수급자 목록:
    // 1. 통장 본인부담금수입에 연결된 수급자 (현재 월)
    // 2. DB에 수납 데이터가 있는 수급자 (현재 월 - 엑셀 업로드 등)
    // ※ 이전 월 미수금만 있는 경우는 표시 안 함 (통장 or 엑셀 업로드 필요)
    const activeList = list.value.filter(b => 
      linkedCbIdxSet.has(b.cb_idx) || 
      dbBeneficiaryIds.has(b.cb_idx)
    );
    
    // 수급자별로 그룹핑
    const beneficiaryMap = new Map<number, CollectionBeneficiary>();
    
    // 수급자 기본 구조 생성
    activeList.forEach(b => {
      const prevData = prevUnpaidMap.get(b.cb_idx);
      beneficiaryMap.set(b.cb_idx, {
        cb_idx: b.cb_idx,
        cb_name: b.cb_name,
        cb_ltc_no: b.cb_ltc_no,
        cb_status: b.cb_status,
        cb_leave_date: b.cb_leave_date,
        charges: [],
        totalCharge: 0,
        totalDeposit: 0,
        totalUnpaid: 0,
        totalOverpaid: 0,
        prevUnpaid: prevData?.unpaid || 0,
        prevOverpaid: prevData?.overpaid || 0,
        cumulativeUnpaid: 0,
        cumulativeOverpaid: 0
      });
    });
    
    // DB 데이터를 수급자별로 할당
    dbItems.forEach((item: any) => {
      const ben = beneficiaryMap.get(item.cb_idx);
      if (ben) {
        // cc_deposit_detail 파싱
        let depositDetail: DepositDetail[] = [];
        if (item.cc_deposit_detail) {
          try {
            depositDetail = typeof item.cc_deposit_detail === 'string' 
              ? JSON.parse(item.cc_deposit_detail) 
              : item.cc_deposit_detail;
          } catch (e) {
            depositDetail = [];
          }
        }
        
        ben.charges.push({
          cc_idx: item.cc_idx,
          cc_scode: item.cc_scode,
          cc_sname: item.cc_sname,
          cc_charge: item.cc_charge || 0,
          cc_charge_date: item.cc_charge_date || '',
          cc_deposit: item.cc_deposit || 0,
          cc_deposit_date: item.cc_deposit_date || '',
          cc_deposit_detail: depositDetail,
          cc_unpaid: item.cc_unpaid || 0,
          cc_overpaid: 0,
          cc_memo: item.cc_memo || ''
        });
      }
    });
    
    // 본인부담금 계정 정보 (112)
    const benefitCode = financeCodes.value.find(fc => fc.scode === '112') || { scode: '112', sname: '본인부담금수입' };
    
    // 각 수급자별로 본인부담금 항목 처리
    beneficiaryMap.forEach(ben => {
      // 통장 매칭 여부 확인 (현재 연월이 통장내역 연월과 같을 때만)
      const depositInfo = depositDataMap[ben.cb_idx];
      ben.hasDeposit = !!depositInfo;
      ben.depositAmount = depositInfo?.amount || 0;
      ben.depositDate = depositInfo?.date || '';
      
      // 본인부담금 항목이 없으면 추가 (scode: 112)
      let benefitCharge = ben.charges.find(c => c.cc_scode === '112');
      if (!benefitCharge) {
        benefitCharge = {
          cc_scode: benefitCode.scode,
          cc_sname: benefitCode.sname,
          cc_charge: 0,
          cc_charge_date: '',
          cc_deposit: 0,
          cc_deposit_date: '',
          cc_unpaid: 0,
          cc_overpaid: 0,
          cc_memo: generateMemo(ben.cb_name, benefitCode.sname)
        };
        ben.charges.unshift(benefitCharge);
      }
      
      // 기존 항목들에 적요가 없으면 자동 생성
      ben.charges.forEach(charge => {
        if (!charge.cc_memo) {
          charge.cc_memo = generateMemo(ben.cb_name, charge.cc_sname);
        }
      });
      
      // 통장 매칭된 경우 플래그 설정
      if (ben.hasDeposit && benefitCharge) {
        benefitCharge._hasDeposit = true;
      }
      
      // 우선순위대로 정렬
      sortChargesByPriority(ben);
      
      // 합계 계산 (입금액 자동 분배 포함)
      calcBeneficiaryTotal(ben);
    });
    
    collectionData.value = Array.from(beneficiaryMap.values());
    
    // 인정번호 입력값 초기화 (인정번호가 없는 수급자용)
    ltcInputValues.value = {};
    collectionData.value.forEach(b => {
      if (!hasValidLtcNo(b)) {
        ltcInputValues.value[b.cb_idx] = '';
      }
    });
  } catch (error) {
    console.error('수금 데이터 로드 오류:', error);
  } finally {
    collectionLoading.value = false;
  }
};

// 인정번호가 유효한지 확인 (정식 인정번호: L + 10자리 숫자만 유효)
const hasValidLtcNo = (ben: CollectionBeneficiary) => {
  const ltcNo = ben.cb_ltc_no?.trim();
  if (!ltcNo) return false;
  // 임시 인정번호는 무효
  if (isTempLtcNo(ltcNo)) return false;
  // 정식 인정번호 형식 확인
  return isValidLtcNo(ltcNo);
};

// 인정번호 저장
const saveLtcNo = async (ben: CollectionBeneficiary) => {
  const inputValue = ltcInputValues.value[ben.cb_idx]?.trim();
  if (!inputValue) return;
  
  // 10자리 숫자만 허용
  const digitsOnly = inputValue.replace(/[^0-9]/g, '');
  if (digitsOnly.length !== 10) {
    alert('인정번호는 L + 10자리 숫자 형식입니다.\n(예: L1234567890)');
    return;
  }
  
  // 이미 저장 중이면 무시
  if (ltcSavingIdx.value === ben.cb_idx) return;
  
  ltcSavingIdx.value = ben.cb_idx;
  
  try {
    const newLtcNo = 'L' + digitsOnly;
    
    // API로 수급자 인정번호 업데이트
    await beneficiaryApi.update(props.memberCode, ben.cb_idx, {
      cb_ltc_no: newLtcNo
    });
    
    // 로컬 데이터 업데이트
    ben.cb_ltc_no = newLtcNo;
    
    // list에도 반영
    const listItem = list.value.find(b => b.cb_idx === ben.cb_idx);
    if (listItem) {
      listItem.cb_ltc_no = newLtcNo;
    }
    
    // 입력값 초기화
    delete ltcInputValues.value[ben.cb_idx];
  } catch (error) {
    console.error('인정번호 저장 오류:', error);
    alert('인정번호 저장에 실패했습니다.');
  } finally {
    ltcSavingIdx.value = null;
  }
};

// 미수금/초과입금 자동 계산
const calcUnpaid = (charge: ChargeItem) => {
  const diff = (charge.cc_charge || 0) - (charge.cc_deposit || 0);
  if (diff > 0) {
    // 청구 > 입금: 미수금 발생
    charge.cc_unpaid = diff;
    charge.cc_overpaid = 0;
  } else if (diff < 0) {
    // 입금 > 청구: 초과입금 발생
    charge.cc_unpaid = 0;
    charge.cc_overpaid = Math.abs(diff);
  } else {
    charge.cc_unpaid = 0;
    charge.cc_overpaid = 0;
  }
};

// 항목을 scode 순서대로 정렬 (112 → 113 → 114 → 115)
const sortChargesByPriority = (ben: CollectionBeneficiary) => {
  ben.charges.sort((a, b) => {
    return (a.cc_scode || '999').localeCompare(b.cc_scode || '999');
  });
};

// 수급자별 입금액 자동 분배 (통장내역 매칭된 경우에만 호출됨)
// 원칙: scode 순서(112→113→114→115)로 청구금액만큼 입금 처리
const distributeDeposits = (ben: CollectionBeneficiary) => {
  // 통장에서 들어온 입금액
  let remainingDeposit = ben.depositAmount || 0;
  
  // scode 순서대로 정렬
  const sortedCharges = [...ben.charges].sort((a, b) => {
    return (a.cc_scode || '999').localeCompare(b.cc_scode || '999');
  });
  
  // 모든 항목의 입금액 초기화
  ben.charges.forEach(c => {
    c.cc_deposit = 0;
  });
  
  // 우선순위대로 청구금액만큼 입금 분배
  for (const charge of sortedCharges) {
    if (remainingDeposit <= 0) break;
    
    const chargeAmount = charge.cc_charge || 0;
    if (chargeAmount > 0) {
      // 청구금액만큼 또는 남은 입금액만큼 할당
      const depositForThis = Math.min(chargeAmount, remainingDeposit);
      charge.cc_deposit = depositForThis;
      remainingDeposit -= depositForThis;
    }
  }
  
  // 남은 입금액이 있으면 본인부담금에 추가 (초과 입금)
  if (remainingDeposit > 0) {
    const benefitCharge = ben.charges.find(c => c.cc_scode === '112');
    if (benefitCharge) {
      benefitCharge.cc_deposit += remainingDeposit;
    }
  }
};

// 수급자별 합계 계산
const calcBeneficiaryTotal = (ben: CollectionBeneficiary) => {
  // DB에서 이미 입금 데이터가 분배되어 있으면 재분배하지 않음
  // (sync-deposit API에서 이미 분배됨)
  const hasDbDeposit = ben.charges.some(c => c.cc_deposit > 0 && c.cc_deposit_detail && c.cc_deposit_detail.length > 0);
  
  // 통장 매칭이 있고, DB에서 분배된 데이터가 없을 때만 실시간 분배
  if (ben.hasDeposit && !hasDbDeposit) {
    distributeDeposits(ben);
  }
  
  // 각 항목의 미수금/초과입금 재계산
  ben.charges.forEach(c => calcUnpaid(c));
  
  // 당월 합계
  ben.totalCharge = ben.charges.reduce((sum, c) => sum + (c.cc_charge || 0), 0);
  ben.totalDeposit = ben.charges.reduce((sum, c) => sum + (c.cc_deposit || 0), 0);
  
  // 당월 순 미수금 계산 (청구 총액 - 입금 총액)
  const currentDiff = ben.totalCharge - ben.totalDeposit;
  ben.totalUnpaid = currentDiff > 0 ? currentDiff : 0;
  ben.totalOverpaid = currentDiff < 0 ? Math.abs(currentDiff) : 0;
  
  // 누적 미수금 계산 (이월 + 당월)
  const prevNet = (ben.prevUnpaid || 0) - (ben.prevOverpaid || 0);
  const currentNet = ben.totalUnpaid - ben.totalOverpaid;
  const cumulativeNet = prevNet + currentNet;
  
  if (cumulativeNet > 0) {
    ben.cumulativeUnpaid = cumulativeNet;
    ben.cumulativeOverpaid = 0;
  } else if (cumulativeNet < 0) {
    ben.cumulativeUnpaid = 0;
    ben.cumulativeOverpaid = Math.abs(cumulativeNet);
  } else {
    ben.cumulativeUnpaid = 0;
    ben.cumulativeOverpaid = 0;
  }
};

// 수금 데이터 저장 (신규 또는 수정)
const saveCharge = async (cbIdx: number, charge: ChargeItem) => {
  if (charge._saving) return;
  
  // 수급자 정보로 적요 확인/생성
  const ben = collectionData.value.find(b => b.cb_idx === cbIdx);
  if (ben && !charge.cc_memo) {
    charge.cc_memo = generateMemo(ben.cb_name, charge.cc_sname);
  }
  
  charge._saving = true;
  
  try {
    if (charge.cc_idx) {
      // 기존 데이터 수정 (적요도 함께 저장)
      await collectionApi.update(charge.cc_idx, {
        cc_charge: charge.cc_charge,
        cc_charge_date: charge.cc_charge_date || null,
        cc_deposit: charge.cc_deposit,
        cc_deposit_date: charge.cc_deposit_date || null,
        cc_memo: charge.cc_memo || undefined
      });
    } else {
      // 신규 데이터 등록
      const res = await collectionApi.create({
        m_code: props.memberCode,
        cb_idx: cbIdx,
        cc_year: collectionYear.value,
        cc_month: collectionMonth.value,
        cc_scode: charge.cc_scode,
        cc_sname: charge.cc_sname,
        cc_charge: charge.cc_charge,
        cc_charge_date: charge.cc_charge_date || null,
        cc_deposit: charge.cc_deposit,
        cc_deposit_date: charge.cc_deposit_date || null,
        cc_memo: charge.cc_memo || undefined,
        cc_biz_gubun: props.bizGubun || undefined
      });
      
      // 생성된 cc_idx 저장
      if (res.data?.data?.cc_idx) {
        charge.cc_idx = res.data.data.cc_idx;
      }
    }
    
    charge._dirty = false;
    
    // 청구금액 변경 시 부모에 알림 (입금 재분배 필요)
    emit('chargeUpdated', cbIdx);
  } catch (error) {
    console.error('수금 데이터 저장 오류:', error);
  } finally {
    charge._saving = false;
  }
};

// 수급자의 모든 항목 입금액 저장 (입금 재분배 후 호출)
const saveAllDeposits = async (ben: CollectionBeneficiary) => {
  const promises = ben.charges
    .filter(c => c.cc_idx) // DB에 저장된 항목만
    .map(c => collectionApi.update(c.cc_idx!, {
      cc_deposit: c.cc_deposit,
      cc_deposit_date: c.cc_deposit_date || null
    }));
  
  try {
    await Promise.all(promises);
  } catch (error) {
    console.error('입금액 일괄 저장 오류:', error);
  }
};

// 청구금액 입력
const onChargeInput = (cbIdx: number, chargeIdx: number, event: Event) => {
  const input = event.target as HTMLInputElement;
  const ben = collectionData.value.find(b => b.cb_idx === cbIdx);
  if (ben && ben.charges[chargeIdx]) {
    ben.charges[chargeIdx].cc_charge = parseNumber(input.value);
    ben.charges[chargeIdx]._dirty = true;
    calcUnpaid(ben.charges[chargeIdx]);
    calcBeneficiaryTotal(ben);
  }
};

const onChargeBlur = async (cbIdx: number, chargeIdx: number) => {
  const ben = collectionData.value.find(b => b.cb_idx === cbIdx);
  if (ben && ben.charges[chargeIdx]?._dirty) {
    await saveCharge(cbIdx, ben.charges[chargeIdx]);
    // 통장 매칭 있으면 입금액 재분배 후 모든 항목 저장
    if (ben.hasDeposit) {
      await saveAllDeposits(ben);
    }
  }
};

// 적요 생성 (수급자명 + 항목명)
const generateMemo = (beneficiaryName: string, sname: string): string => {
  return `${beneficiaryName} ${sname}`;
};

// 비급여 항목 빠른 추가 (클릭 한 번으로) - financeCode 기반
const addChargeQuick = async (cbIdx: number, financeCode: FinanceCode) => {
  const ben = collectionData.value.find(b => b.cb_idx === cbIdx);
  if (!ben) return;
  
  // 같은 scode 항목이 이미 있는지 확인
  const exists = ben.charges.some(c => c.cc_scode === financeCode.scode);
  if (exists) {
    alert(`${financeCode.sname} 항목이 이미 있습니다.`);
    return;
  }
  
  // 청구일 기본값: 본인부담금 청구일 → 없으면 현재 연월 + 01일
  const benefitCharge = ben.charges.find(c => c.cc_scode === '112');
  const defaultChargeDate = benefitCharge?.cc_charge_date || 
    `${collectionYear.value}-${String(collectionMonth.value).padStart(2, '0')}-01`;
  
  // 적요 자동 생성 (수급자명 + 항목명)
  const memo = generateMemo(ben.cb_name, financeCode.sname);
  
  try {
    // 바로 DB에 저장
    const res = await collectionApi.create({
      m_code: props.memberCode,
      cb_idx: cbIdx,
      cc_year: collectionYear.value,
      cc_month: collectionMonth.value,
      cc_scode: financeCode.scode,
      cc_sname: financeCode.sname,
      cc_charge: 0,
      cc_charge_date: defaultChargeDate,
      cc_deposit: 0,
      cc_memo: memo,
      cc_biz_gubun: props.bizGubun || undefined
    });
    
    // 로컬 데이터에 추가
    ben.charges.push({
      cc_idx: res.data?.data?.cc_idx,
      cc_scode: financeCode.scode,
      cc_sname: financeCode.sname,
      cc_charge: 0,
      cc_charge_date: defaultChargeDate,
      cc_deposit: 0,
      cc_deposit_date: '',
      cc_unpaid: 0,
      cc_overpaid: 0,
      cc_memo: memo
    });
    
    // scode 순서대로 정렬
    sortChargesByPriority(ben);
    
    // 합계 재계산
    calcBeneficiaryTotal(ben);
    
    // 통장 매칭 있으면 입금액 재분배 후 모든 항목 저장
    if (ben.hasDeposit) {
      await saveAllDeposits(ben);
    }
  } catch (error) {
    console.error('항목 추가 오류:', error);
    alert('항목 추가에 실패했습니다.');
  }
};

// 수급자에게 추가 가능한 항목 (아직 등록되지 않은 비급여 항목들)
const getAvailableItems = (ben: CollectionBeneficiary): FinanceCode[] => {
  const existingScodes = new Set(ben.charges.map(c => c.cc_scode));
  return nonBenefitItems.value.filter(fc => !existingScodes.has(fc.scode));
};

// 항목추가 드롭다운 토글
const toggleAddDropdown = (cbIdx: number) => {
  if (addDropdownOpen.value === cbIdx) {
    addDropdownOpen.value = null;
  } else {
    addDropdownOpen.value = cbIdx;
  }
};

// 드롭다운에서 항목 선택 시
const selectAddItem = (cbIdx: number, fc: FinanceCode) => {
  addChargeQuick(cbIdx, fc);
  addDropdownOpen.value = null;  // 드롭다운 닫기
};

// 항목 삭제 (본인부담금 포함 모든 항목 삭제 가능)
const removeCharge = async (cbIdx: number, chargeIdx: number) => {
  const ben = collectionData.value.find(b => b.cb_idx === cbIdx);
  if (!ben) return;
  
  const charge = ben.charges[chargeIdx];
  if (!charge) return;
  
  // 매칭된 본인부담금은 삭제 불가
  if (charge.cc_scode === '112' && ben.hasDeposit) {
    alert('통장내역에 매칭된 본인부담금수입은 삭제할 수 없습니다.\n\n먼저 통장내역에서 수급자 연결을 해제하세요.');
    return;
  }
  
  // 비매칭 본인부담금 삭제 시 확인
  if (charge.cc_scode === '112') {
    if (!confirm(`${ben.cb_name}님의 본인부담금수입을 삭제하시겠습니까?`)) {
      return;
    }
  }
  
  // DB에 저장된 항목이면 soft delete
  if (charge.cc_idx) {
    try {
      await collectionApi.remove(charge.cc_idx);
    } catch (error) {
      console.error('항목 삭제 오류:', error);
      alert('항목 삭제에 실패했습니다.');
      return;
    }
  }
  
  // 로컬에서 제거
  ben.charges.splice(chargeIdx, 1);
  
  // 본인부담금 삭제 시 부모에 알림 (매칭 새로고침)
  if (charge.cc_scode === '112') {
    emit('collectionDeleted');
  }
  
  // 남은 항목이 있으면 총계 재계산
  if (ben.charges.length > 0) {
    calcBeneficiaryTotal(ben);
    
    // 통장 매칭 있으면 입금액 재분배 후 모든 항목 저장
    if (ben.hasDeposit) {
      await saveAllDeposits(ben);
    }
  }
};

// ========== 선택/삭제 관련 (수급자 단위) ==========

// 통장 매칭된 본인부담금이 있는 수급자인지 확인
const hasMatchedBenefit = (ben: CollectionBeneficiary): boolean => {
  // hasDeposit이 true면 통장내역에 매칭된 것
  return ben.hasDeposit === true;
};

// 삭제 가능한 수급자 목록 (통장 매칭 없는 수급자만)
const deletableBeneficiaries = computed(() => {
  return collectionData.value.filter(ben => !hasMatchedBenefit(ben));
});

// 전체 선택 여부 (삭제 가능한 수급자 기준)
const isAllSelected = computed(() => {
  if (deletableBeneficiaries.value.length === 0) return false;
  return deletableBeneficiaries.value.every(ben => selectedBeneficiaries.value.has(ben.cb_idx));
});

// 일부 선택 여부
const isPartialSelected = computed(() => {
  if (deletableBeneficiaries.value.length === 0) return false;
  const selectedCount = deletableBeneficiaries.value.filter(ben => selectedBeneficiaries.value.has(ben.cb_idx)).length;
  return selectedCount > 0 && selectedCount < deletableBeneficiaries.value.length;
});

// 전체 선택/해제 토글 (삭제 가능한 수급자만)
const toggleSelectAll = () => {
  if (isAllSelected.value) {
    selectedBeneficiaries.value.clear();
  } else {
    deletableBeneficiaries.value.forEach(ben => selectedBeneficiaries.value.add(ben.cb_idx));
  }
};

// 개별 수급자 선택 토글
const toggleBeneficiarySelection = (cbIdx: number) => {
  // 매칭된 수급자는 선택 불가
  const ben = collectionData.value.find(b => b.cb_idx === cbIdx);
  if (ben && hasMatchedBenefit(ben)) return;
  
  if (selectedBeneficiaries.value.has(cbIdx)) {
    selectedBeneficiaries.value.delete(cbIdx);
  } else {
    selectedBeneficiaries.value.add(cbIdx);
  }
};

// 선택된 수급자의 모든 항목 삭제
const deleteSelectedBeneficiaries = async () => {
  if (selectedBeneficiaries.value.size === 0) return;
  
  // 선택된 수급자들의 이름 목록
  const selectedNames = collectionData.value
    .filter(ben => selectedBeneficiaries.value.has(ben.cb_idx))
    .map(ben => ben.cb_name)
    .join(', ');
  
  const confirmMsg = `선택한 ${selectedBeneficiaries.value.size}명의 수납 데이터를 모두 삭제하시겠습니까?\n\n대상: ${selectedNames}\n\n통장내역의 수급자 매칭도 해제됩니다.`;
  if (!confirm(confirmMsg)) return;
  
  isDeletingCharges.value = true;
  
  try {
    // 선택된 수급자들의 모든 cc_idx 수집 (DB에 저장된 것만)
    const allCcIdxToDelete: number[] = [];
    collectionData.value.forEach(ben => {
      if (selectedBeneficiaries.value.has(ben.cb_idx)) {
        ben.charges.forEach(charge => {
          if (charge.cc_idx) allCcIdxToDelete.push(charge.cc_idx);
        });
      }
    });
    
    // DB 삭제 (저장된 항목이 있는 경우)
    if (allCcIdxToDelete.length > 0) {
      const deletePromises = allCcIdxToDelete.map(ccIdx => 
        collectionApi.remove(ccIdx)
      );
      await Promise.all(deletePromises);
    }
    
    alert(`${selectedBeneficiaries.value.size}명의 수납 데이터가 삭제되었습니다.`);
    selectedBeneficiaries.value.clear();
    
    // 부모에 알림 (매칭 새로고침)
    emit('collectionDeleted');
    
    // 데이터 새로고침
    await loadCollectionData();
  } catch (error) {
    console.error('선택 삭제 오류:', error);
    alert('삭제 중 오류가 발생했습니다.');
  } finally {
    isDeletingCharges.value = false;
  }
};

// 전체 삭제 (매칭 안 된 수급자만)
const deleteAllBeneficiaries = async () => {
  // 삭제 가능한 수급자만 대상
  const targetBeneficiaries = deletableBeneficiaries.value;
  if (targetBeneficiaries.length === 0) {
    alert('삭제 가능한 수납 데이터가 없습니다.\n\n통장내역에 매칭된 본인부담금수입은 삭제할 수 없습니다.');
    return;
  }
  
  const matchedCount = collectionData.value.length - targetBeneficiaries.length;
  let confirmMsg = `${collectionYear.value}년 ${collectionMonth.value}월의 수납 데이터를 삭제하시겠습니까?\n\n삭제 대상: ${targetBeneficiaries.length}명`;
  if (matchedCount > 0) {
    confirmMsg += `\n(통장 매칭된 ${matchedCount}명은 제외)`;
  }
  confirmMsg += '\n\n이 작업은 되돌릴 수 없습니다.';
  
  if (!confirm(confirmMsg)) return;
  
  isDeletingCharges.value = true;
  
  try {
    // 삭제 가능한 수급자의 cc_idx 수집 (DB에 저장된 것만)
    const allCcIdxToDelete: number[] = [];
    targetBeneficiaries.forEach(ben => {
      ben.charges.forEach(charge => {
        if (charge.cc_idx) allCcIdxToDelete.push(charge.cc_idx);
      });
    });
    
    // DB 삭제 (저장된 항목이 있는 경우)
    if (allCcIdxToDelete.length > 0) {
      const deletePromises = allCcIdxToDelete.map(ccIdx => 
        collectionApi.remove(ccIdx)
      );
      await Promise.all(deletePromises);
    }
    
    alert(`${targetBeneficiaries.length}명의 수납 데이터가 삭제되었습니다.`);
    selectedBeneficiaries.value.clear();
    
    // 부모에 알림 (매칭 새로고침)
    emit('collectionDeleted');
    
    // 데이터 새로고침
    await loadCollectionData();
  } catch (error) {
    console.error('전체 삭제 오류:', error);
    alert('삭제 중 오류가 발생했습니다.');
  } finally {
    isDeletingCharges.value = false;
  }
};

// ========== 누적 미수금 관련 ==========

// 누적 미수금 로드 (현재 설정된 연월까지)
const loadCumulativeUnpaid = async () => {
  try {
    const params: { year: number; month: number; bizGubun?: string } = {
      year: collectionYear.value,
      month: collectionMonth.value
    };
    if (props.bizGubun) {
      params.bizGubun = props.bizGubun;
    }
    const res = await collectionApi.getCumulativeUnpaid(props.memberCode, params);
    const data = res.data?.data || {};
    cumulativeUnpaidTotal.value = {
      unpaid: data.cumulativeUnpaid || 0,
      overpaid: data.cumulativeOverpaid || 0
    };
  } catch (error) {
    console.error('누적 미수금 조회 오류:', error);
  }
};

// 미수이력 다이얼로그 열기
const openUnpaidHistoryDialog = () => {
  unpaidHistoryDialog.value = true;
  // 기본 기간 설정: 시작일 비움(가장 과거), 종료일은 현재 설정된 연월의 마지막 날
  const lastDay = new Date(collectionYear.value, collectionMonth.value, 0).getDate();
  const monthStr = String(collectionMonth.value).padStart(2, '0');
  const endDateStr = `${collectionYear.value}-${monthStr}-${String(lastDay).padStart(2, '0')}`;
  
  unpaidHistorySearch.value = {
    name: '',
    startDate: '',
    endDate: endDateStr
  };
  loadUnpaidHistory(1);
};

// 미수이력 로드 (현재 설정된 연월까지)
const loadUnpaidHistory = async (page: number = 1) => {
  unpaidHistoryLoading.value = true;
  try {
    const params: {
      year: number;
      month: number;
      searchName?: string;
      startDate?: string;
      endDate?: string;
      bizGubun?: string;
      page: number;
      limit: number;
    } = {
      year: collectionYear.value,
      month: collectionMonth.value,
      page,
      limit: 50
    };
    
    if (unpaidHistorySearch.value.name.trim()) {
      params.searchName = unpaidHistorySearch.value.name.trim();
    }
    if (unpaidHistorySearch.value.startDate) {
      params.startDate = unpaidHistorySearch.value.startDate;
    }
    if (unpaidHistorySearch.value.endDate) {
      params.endDate = unpaidHistorySearch.value.endDate;
    }
    if (props.bizGubun) {
      params.bizGubun = props.bizGubun;
    }
    
    const res = await collectionApi.getUnpaidHistory(props.memberCode, params);
    const data = res.data?.data || {};
    
    unpaidHistoryData.value = {
      list: data.list || [],
      total: data.total || 0,
      totalUnpaid: data.totalUnpaid || 0,
      page: data.page || 1,
      totalPages: data.totalPages || 1
    };
  } catch (error) {
    console.error('미수이력 조회 오류:', error);
  } finally {
    unpaidHistoryLoading.value = false;
  }
};

// 미수이력 검색
const searchUnpaidHistory = () => {
  loadUnpaidHistory(1);
};

// 미수이력 검색 초기화 (기본값: 시작일 비움, 종료일은 현재 설정된 연월의 마지막 날)
const resetUnpaidHistorySearch = () => {
  const lastDay = new Date(collectionYear.value, collectionMonth.value, 0).getDate();
  const monthStr = String(collectionMonth.value).padStart(2, '0');
  const endDateStr = `${collectionYear.value}-${monthStr}-${String(lastDay).padStart(2, '0')}`;
  
  unpaidHistorySearch.value = {
    name: '',
    startDate: '',
    endDate: endDateStr
  };
  loadUnpaidHistory(1);
};

// 날짜 포맷 (YYYY-MM-DD → YYYY년 M월 D일)
const formatDateKorean = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '-';
  try {
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const year = parseInt(parts[0]);
      const month = parseInt(parts[1]);
      const day = parseInt(parts[2]);
      return `${year}년 ${month}월 ${day}일`;
    }
    return dateStr;
  } catch {
    return dateStr || '-';
  }
};

// Watch — loadList / loadCollectionData 정의 이후에 등록 (immediate TDZ 방지)
watch(() => props.modelValue, (val) => {
  if (props.embedded) return;
  if (dialogOpen.value !== val) {
    dialogOpen.value = val;
  }
}, { immediate: true });

watch(dialogOpen, (val) => {
  if (props.embedded) return;
  if (props.modelValue !== val) {
    emit('update:modelValue', val);
  }
});

watch(isActive, async (val) => {
  if (val) {
    resetAll();
    await loadList();
    await loadCollectionData();
  }
}, { immediate: true });

watch(() => props.depositData, async () => {
  if (isActive.value) {
    await loadCollectionData();
  }
}, { deep: true });

watch(
  () => props.initialSearchName,
  (name) => {
    if (props.embedded && isActive.value) {
      collectionSearchName.value = name || '';
    }
  }
);

watch(
  () => [props.year, props.month, props.memberCode, props.bizGubun] as const,
  async () => {
    if (props.embedded && isActive.value) {
      resetAll();
      await loadList();
      await loadCollectionData();
    }
  }
);

// 수납 데이터 없음 여부 (임베드 페이지의 시작 가이드 표시용)
const isCollectionEmpty = computed(() => !collectionLoading.value && sortedCollectionData.value.length === 0);

defineExpose({
  refresh: async () => {
    if (!isActive.value) return;
    await loadList();
    await loadCollectionData();
  },
  isCollectionEmpty,
});

</script>
